import { defineStore } from "pinia";
import { ref } from "vue";
import { useToast } from "vue-toastification";
import authService from "@/services/auth";
import userService from "@/services/user";
import useEncryption from "@/utils/helpers/encryption";
import router from "@/router";
import { startKeepAlive, stopKeepAlive } from "@/utils/sessionKeepAlive";

export const useAuthStore = defineStore("auth", () => {
  const toast = useToast();
  const { encryptionPassword } = useEncryption();
  const loading = ref(false);

  async function register(payload) {
    loading.value = true;
    try {
      const res = await authService.register(payload);
      toast.success(res.message);
      router.push("/login");
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function login(payload) {
    loading.value = true;
    payload.password = await encryptionPassword(payload.password);
    try {
      const data = await authService.login(payload);
      const user = data.data.data;
      localStorage.setItem("user", JSON.stringify(user));
      localStorage.setItem(
        "userDetails",
        JSON.stringify(user?.user?.companies?.[0]?.pivot),
      );

      if (user?.user?.companies?.length === 1) {
        localStorage.setItem(
          "currentCompany",
          JSON.stringify(user.user.companies[0]),
        );
        localStorage.setItem(
          "currentCompanyId",
          user.user.companies[0]?.id,
        );
      }

      localStorage.setItem(
        "tableName",
        user?.user?.companies?.[0]?.table_name,
      );

      startKeepAlive();
      return data;
    } catch (err) {
      toast.error(err?.response?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function fetchUserCompanies() {
    loading.value = true;
    try {
      const res = await userService.getAllUserCompanies();
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function forgetPassword(payload) {
    loading.value = true;
    try {
      const res = await authService.forgetPassword(payload);
      toast.success(res.data.message);
      return res;
    } catch (err) {
      toast.error(err?.response?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function resetPassword(payload) {
    loading.value = true;
    payload.password = await encryptionPassword(payload.password);
    payload.code = await encryptionPassword(payload.code);
    try {
      const res = await authService.resetPassword(payload);
      toast.success(res.data.message);
      return res;
    } catch (err) {
      toast.error(err?.response?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function checkUserPassword(payload) {
    loading.value = true;
    payload.password = await encryptionPassword(payload.password);
    try {
      const res = await authService.checkUserPassword(payload);
      return res;
    } catch (err) {
      toast.error(err?.data?.message);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function logout() {
    stopKeepAlive();
    try {
      await authService.logout();
    } catch {
      // Server call may fail if session already expired — proceed with local cleanup regardless
    }
    localStorage.clear();
    router.push("/login");
  }

  return {
    loading,
    register,
    login,
    fetchUserCompanies,
    forgetPassword,
    resetPassword,
    checkUserPassword,
    logout,
  };
});
